import { backendSingleExecution } from '@disy/lib.backend/single-execution';
import { requireNonNullable } from '@disy/lib/custom-error';
import { uniqueId } from '@disy/lib/unique-id';

import type { AttributeValueStyledPropertyRuleDto } from 'cadenza/api-client/dto/attribute-value-styled-property-rule-dto';
import type { IntervalClassificationDto } from 'cadenza/api-client/interval-classification-api';
import { getIntervalClassifications, getMeasureMinMax } from 'cadenza/api-client/interval-classification-api';
import type { StylePropertyValue } from 'cadenza/api-client/model/style-property-value';
import { getDefaultColorClasses } from 'cadenza/api-client/objecttypes-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import { DEFAULT_UNIFORM_COLOR } from 'cadenza/attribute-coloring-panel/other-values-section/other-values-section-constants';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { AttributeBundleSettingsKey } from 'cadenza/workbook/model/attribute-bundle-settings-key.gen';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type {
  BaseIntervalClassificationClassDto,
  IntervalClassificationParametersDto,
  MinMax,
} from 'cadenza/workbook/model/interval-classification-model';
import type { MapAttributeBundle } from 'cadenza/workbook/model/map-attribute-bundle.gen';
import { getAttributeById, getWorkbookNameAndHash } from 'cadenza/workbook/workbook-core-selectors.state';
import {
  getClassificationClassLabel,
  getIncludesLimit,
} from 'cadenza/workbook/workbook-panel/settings/classification/workbook-classification-utils';
import { getClassificationActiveAttributeId } from 'cadenza/workbook/workbook-panel/settings/map/advanced-data-layer-settings/advanced-style-property-classification-settings-utils';
import { getClassificationType } from 'cadenza/workbook/workbook-panel/settings/map/settings/classes-setting/classes-setting-utils';
import { updateDataViewMetadata } from 'cadenza/workbook/workbook.state';

import i18n from './combined-classification-classes-setting.properties';

export const COMBINED_MODE_UNAVAILABLE_BUNDLES: MapAttributeBundle[] = ['IMAGE', 'SYMBOL_SIZE', 'COLOR_CLASSIFICATION'];
export const STANDARD_MODE_UNAVAILABLE_BUNDLES: MapAttributeBundle[] = ['COMBINED_CLASSIFICATION'];

export interface SeparatedCombinedRules {
  customClassifications: AttributeValueStyledPropertyRuleDto[];
  otherValuesClassification: AttributeValueStyledPropertyRuleDto;
}

export function getUniqueValuesClassClassifications(activeAttributeId: ActiveAttributeId) {
  const attribute = getAttributeById(getStore().getState(), activeAttributeId);
  return attribute ? split(attribute).customClassifications : [];
}

export function getOtherValueClassification(activeAttributeId: ActiveAttributeId) {
  const attribute = getAttributeById(getStore().getState(), activeAttributeId);
  return attribute?.attributeStyleRules?.at(0);
}

export function requireIntervalOtherValueClassification(activeAttributeId: ActiveAttributeId) {
  const attribute = getAttributeById(getStore().getState(), activeAttributeId);
  const other = requireNonNullable(getOtherValueClassification(activeAttributeId));
  return toBaseIntervalClassification(other, attribute);
}

export function getIntervalClassClassifications(activeAttributeId: ActiveAttributeId) {
  const attribute = getAttributeById(getStore().getState(), activeAttributeId);
  return attribute ?
      split(attribute).customClassifications.map((rule) => toIntervalClassification(rule, attribute))
    : [];
}

function split(attribute: WorkbookAttributeDto) {
  const unfilteredRows = requireNonNullable(attribute.attributeStyleRules);
  return separateOtherRuleFromCustomRules(unfilteredRows);
}

export function separateOtherRuleFromCustomRules(
  attributeStyleRules: AttributeValueStyledPropertyRuleDto[],
): SeparatedCombinedRules {
  const [otherValuesClassification, ...customClassifications] = attributeStyleRules;
  return { otherValuesClassification, customClassifications };
}

export function toIntervalClassification(rule: AttributeValueStyledPropertyRuleDto, attribute: WorkbookAttributeDto) {
  return {
    ...toBaseIntervalClassification(rule, attribute),
    transientClassKey: uniqueId(),
    lowerLimit: Number(rule.attributeValues[0].value),
    upperLimit: Number(rule.attributeValues[1].value),
  };
}

function toBaseIntervalClassification(rule: AttributeValueStyledPropertyRuleDto, attribute: WorkbookAttributeDto) {
  return {
    label: rule.label,
    styleRules: rule.styleRules.map((styleRule) => ({
      ...styleRule,
      value: requireNonNullable(styleRule.value),
    })),
    includesLimit: getIncludesLimit(attribute),
  };
}

const getMinMax = backendSingleExecution<MinMax | undefined>(getMeasureMinMax);
const getClassifications =
  backendSingleExecution<IntervalClassificationDto<StylePropertyValue>>(getIntervalClassifications);

export async function generateDefaultIntervalClasses(
  classificationAttributeId: AttributeId,
  dataViewId: DataViewId,
): Promise<BaseIntervalClassificationClassDto[]> {
  const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
  const attribute = getAttributeById(getStore().getState(), classificationAttributeId);
  const minMax = await getMinMax(workbookNameAndHash, dataViewId, classificationAttributeId);
  getStore().dispatch(updateDataViewMetadata(dataViewId, { hasMinMax: minMax != null }));
  if (!minMax) {
    return [createDefaultOtherClassification()];
  }
  const classificationParams: IntervalClassificationParametersDto = {
    type: 'LINEAR_BY_CLASS',
    numberOfClasses: 5,
    roundClassLimits: true,

    smallestValue: minMax.min,
    largestValue: minMax.max,
    label: getClassificationClassLabel(attribute),
    highValueClassesFirst: false,
    includesLimit: getIncludesLimit(attribute),
    valueProvider: { type: 'INTEGER_RANGE', min: 10, max: 10 },
  };
  const { classifications } = await getClassifications(
    workbookNameAndHash,
    classificationAttributeId,
    classificationParams,
  );
  classifications.forEach((classification) => {
    classification.styleRules.push({ type: 'foregroundColor', value: DEFAULT_UNIFORM_COLOR });
  });
  return [createDefaultOtherClassification(), ...classifications];
}

function createDefaultOtherClassification() {
  return {
    attributeValues: [],
    styleRules: [{ type: 'foregroundColor' as const, value: DEFAULT_UNIFORM_COLOR }],
    label: i18n('otherClassName'),
  };
}

export function removeBundleCommand(dataViewId: DataViewId, attributeBundleName: MapAttributeBundle) {
  return Command.CHANGE_ATTRIBUTE_BUNDLE_SETTINGS({
    dataViewId,
    attributeBundleName,
    settings: {
      [AttributeBundleSettingsKey.toPersistenceKey('BUNDLE_VISIBLE')]: String(false),
    },
  });
}

export async function createDefaultClassesCommands(dataViewId: DataViewId, attributeId: AttributeId) {
  const classificationType = getClassificationType(attributeId);
  const commands = [];
  if (classificationType === 'UNIQUE_VALUES') {
    commands.push(await generateDefaultUniqueValueClassesCommand(dataViewId, attributeId));
  } else {
    commands.push(await generateDefaultIntervalClassesCommand(dataViewId, attributeId));
  }
  return commands;
}

async function generateDefaultIntervalClassesCommand(dataViewId: DataViewId, newAttributeId: AttributeId) {
  const currentAttribute = getClassificationActiveAttributeId(dataViewId, 'COMBINED_CLASSIFICATION');
  const includesLimit = currentAttribute ? getIncludesLimit(currentAttribute) : 'INCLUDES_LOWER_LIMIT';
  const intervalClassificationClasses = await generateDefaultIntervalClasses(newAttributeId, dataViewId);
  return Command.SET_DATA_VIEW_ATTRIBUTE_INTERVAL_CLASSIFICATION({
    dataViewId,
    attributeBundleName: 'COMBINED_CLASSIFICATION',
    intervalClassificationClasses,
    includesLimit,
  });
}

async function generateDefaultUniqueValueClassesCommand(dataViewId: DataViewId, newAttributeId: AttributeId) {
  const attributeColorRules = await getDefaultColorClasses(newAttributeId);
  return Command.SET_DATA_VIEW_ATTRIBUTE_UNIQUE_VALUES_CLASSIFICATION({
    dataViewId,
    attributeBundleName: 'COMBINED_CLASSIFICATION',
    attributeValueStyledPropertyRules: [
      createDefaultOtherClassification(),
      ...attributeColorRules.colorRules.map((attribute) => ({
        attributeValues: attribute.attributeValues,
        label: attribute.label,
        styleRules: [{ type: 'foregroundColor' as const, value: attribute.color }],
      })),
    ],
  });
}
